//--------------------------------------------------------------------//
//******************** DeploymentsService ******************************//
//--------------------------------------------------------------------//
// API communication layer for deployment CRUD + status/logs.
// Every endpoint now requires the caller's GudHub token and is scoped to
// deployments that token owns. getList/getOne/getLogs are GET requests, so
// they send the token via an Authorization header rather than the
// request() helper (which is POST-only and injects it into the JSON body).
import { GUDHUB_SETTINGS } from '../../config.js';
import { request, getAuthData } from './deployments.request.js';
require('../../core/localizationService/localizationService.js');

angular.module('deploymentsTableModule', ['localizationModule'])

    .service('DeploymentsService', [function () {
        // Fetches all deployments visible to the caller (the backend scopes
        // this to deployments owned by the authenticated user's token).
        this.getList = async function () {
            const authData = await getAuthData();
            const url = `${GUDHUB_SETTINGS.deployment_manager_server_url}/get-deployments`;
            const response = await fetch(url, { headers: { Authorization: authData.token } });
            return response.json();
        };

        // Fetches a single deployment by its MongoDB _id. 404s if it isn't
        // owned by the authenticated user.
        this.getOne = async function (deploymentId) {
            const authData = await getAuthData();
            const url = `${GUDHUB_SETTINGS.deployment_manager_server_url}/get-deployment/${encodeURIComponent(deploymentId)}`;
            const response = await fetch(url, { headers: { Authorization: authData.token } });
            return response.json();
        };

        // Creates a new deployment. { repo_url, build_command, run_command, active }
        // Deploys synchronously when active is truthy (the backend default) — this
        // call can take a while (npm install etc.), callers must show a blocking loader.
        this.create = function (deploymentData) {
            return request('/create-deployment', deploymentData);
        };

        // Updates a deployment. { deployment_id, repo_url?, build_command?, run_command?, active? }
        // Changing repo_url/build_command/run_command triggers a full synchronous redeploy.
        this.update = function (deploymentData) {
            return request('/update-deployment', deploymentData);
        };

        // Deletes a deployment by its MongoDB _id.
        this.delete = function (deploymentId) {
            return request('/delete-deployment', { deployment_id: deploymentId });
        };

        // Runs `git pull` inside the deployment's existing container — lighter
        // weight than update()'s full redeploy. Rebuilds/restarts only if there
        // were new commits. No gudhub_token needed.
        this.pull = function (deploymentId) {
            return request('/pull-deployment', { deployment_id: deploymentId });
        };

        // Forces a full fresh redeploy (tear down + reclone + build + run) even
        // if nothing in the config changed. Slower than pull() but guarantees a
        // completely clean container.
        this.deploy = function (deploymentId) {
            return request('/deploy-deployment', { deployment_id: deploymentId });
        };

        // Stops and removes the container entirely, keeping the deployment
        // record (and its SSH key / persistent /data storage) intact. The next
        // deploy() starts completely from scratch.
        this.kill = function (deploymentId) {
            return request('/kill-deployment', { deployment_id: deploymentId });
        };

        // Tails the deployment's stdout/stderr log.
        // No container yet (never deployed, stopped, or killed) is a normal
        // state, not an error — the backend returns 200 with log: null for
        // that case, so noContainer is derived from the body, not the status.
        this.getLogs = async function (deploymentId, tail = 200) {
            const authData = await getAuthData();
            const url = `${GUDHUB_SETTINGS.deployment_manager_server_url}/get-deployment-logs/${encodeURIComponent(deploymentId)}?tail=${tail}`;
            const response = await fetch(url, { headers: { Authorization: authData.token } });
            const result = await response.json();

            if (!response.ok) {
                throw new Error(result.error || 'Failed to fetch deployment logs');
            }

            return { log: result.log, noContainer: result.log === null };
        };

        // Maps a deployment's status to a human-readable label.
        this.getStatusLabel = function (status) {
            const labels = {
                running: 'Running',
                stopped: 'Stopped',
                deploying: 'Deploying...',
                error: 'Error'
            };
            return labels[status] || status;
        };

        // Maps a deployment's status to a CSS class for the status badge.
        this.getStatusClass = function (status) {
            return status || 'stopped';
        };

        // Turns multi-line textarea text (one command per line) into the single
        // string build_command/run_command expects: trims each line, drops blank
        // lines, and joins the rest with ' && ' so the sequence stops at the
        // first failing command. Returns '' if nothing is left (e.g. a plain
        // static-HTML repo with no build/run step needed).
        this.joinCommandLines = function (text) {
            if (!text) return '';
            return text
                .split('\n')
                .map((line) => line.trim())
                .filter((line) => line.length > 0)
                .join(' && ');
        };
    }]);
